/** @jsxImportSource theme-ui */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Image, Text, useColorMode } from "theme-ui";
import FilterIcon from '../../../assets/images/Icon/filterIcon.png';
import SelectIcon from '../../../assets/images/Icon/select.svg';
import { DashboardFilterPropType } from "../../../../types/dashboard.types";
import "../style/Filter.css";
import FilterButton from "../Components/FilterButton";
import OptionList from "../Components/OptionList";
import CustomFilterSelect from "../Components/CustomFilterSelect";
import CustomMonthSelect from "../Components/CustomMothSelect";
import { useSelector } from "react-redux";
import TradesOptionList from "../Components/TradesOptionList";

const DashboardFilters: React.FC<DashboardFilterPropType> = ({
    value,
    popoverRef,
    isPopoverOpen,
    handleOptionClick,
    togglePopover,
    showMonthYearPicker,
    months,
    showProjectManagerList,
    projectManagerList,
    toggleManagerHandle,
    toggleYearMonthHandle,
    handleAllManagerSelection,
    handleAllMonthSelection,
    handleAllYearSelection,
    loading,
}) => {
    const [search, setSearch] = useState("");
    const { createprojectMasterData } = useSelector((state: any) => state.project);
    const { allDepartments } = createprojectMasterData;
    const { years } = useSelector((state: any) => state.dashboardData);

    const checkProjectManagerSelected = useCallback((id: string) => {
        return value.projectManager.includes(id);
    }, [value.projectManager]);

    const checkProjectWorkerSelected = useCallback((id: string) => {
        return value.projectWorker.includes(id);
    }, [value.projectWorker]);

    const checkYearSelected = useCallback((year: number) => {
        return value.year.includes(year);
    }, [value.year]);

    const checkMonthSelected = useCallback((month: string) => {
        return value.month.includes(month);
    }, [value.month]);

    const timeOptions = useMemo(() => [
        "All Time",
        "YTD",
        "Last 6 Months",
        "Last 3 Months",
        "This Month",
        "Custom Selection",
    ], []);

    const projectTypeOptions = useMemo(() => [
        "All Projects",
        "Residential",
        "Commercial",
        "Industrial",
        "Mix - used",
    ], []);
    useEffect(() => {
        if (isPopoverOpen === "" || isPopoverOpen === null) {
            setSearch("")
        }
    }, [isPopoverOpen])

    const tradesSelectedOptions = ["MECHANICAL", "ELECTRICAL", "PLUMBING", "STRUCTURAL"]
    const filteredDepartments = Array.isArray(allDepartments?.data) ? allDepartments?.data.filter((dept: any) =>
        tradesSelectedOptions.includes(dept.name.toUpperCase())
    ) : [];
    let tradesOptions = [{ name: "All", _id: "" }, ...filteredDepartments,]
    const tradeGroups = tradesSelectedOptions.includes(value.trades);
    let managerDataKey = tradeGroups ? "projectWorker" : "projectManager"

    const handleSearch = (event: any) => {
        setSearch(event.target.value)
    }

    const filteredManagersList = useMemo(() => {
        return projectManagerList.filter((item: any) =>
            String(item.FirstName + " " + item.LastName)
                .toLowerCase()
                .includes(search.toLowerCase())
        );
    }, [projectManagerList, search,]);
    const [colorMode] = useColorMode();
    const bgFilter = colorMode === 'light' ? "#ffffff" : "#323c56";
    const titleHeader = colorMode === 'light' ? "#000" : "#dadada";
    const filteredMonths = useMemo(() => {
        return months.filter((item: any) =>
            item
                .toLowerCase()
                .includes(search.toLowerCase())
        );
    }, [months, search,]);

    const filteredYears = useMemo(() => {
        if (!search) return years;
        const term = search.toString();
        return years.filter((year: any) =>
            year.toString().includes(term)
        );
    }, [years, search]);


    const checkAllMonthSelect = () => {
        return search !== "" && filteredMonths.length === 0 ? false : value?.month.length > 0 ? filteredMonths.every((month: string) => value.month.includes(month)) : false
    };

    const checkAllYearSelect = () => {
        return search !== "" && filteredYears.length === 0 ? false : value?.year.length > 0 ? filteredYears.every((year: number) => value.year.includes(year)) : false
    };

    const checkSearchExist = () => {
        return search !== ""
    };

    function capitalizeFirst(str: string): string {
        if (!str) return str;
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    return (
        <div ref={popoverRef} style={{ position: 'relative', display: 'flex' }}>
            <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: ["flex-start", "flex-start", "flex-start", "center"], gap: "10px", flexDirection: ["column", "row"] }}>

                {/* Time */}
                <Box sx={{ position: 'relative' }}>
                    <FilterButton onClick={() => !loading && togglePopover("time")} label={value.time || "Time"} sx={{ minWidth: '113px' }} />
                    {isPopoverOpen === "time" && !loading && (
                        <Box sx={{ position: 'absolute', gap: '8px', right: 2, zIndex: 1, backgroundColor: bgFilter, boxShadow: '0px 0px 16px rgba(0,0,0,0.06)', borderRadius: '4px', fontSize: '15px', maxWidth: "max-content", color: titleHeader, width: '181px' }}>
                            <OptionList options={timeOptions} selected={value.time || null} onSelect={(opt) => handleOptionClick(opt === "Custom Selection" ? "Custom Selection" : opt, "time")} />
                        </Box>
                    )}
                </Box>

                {/* Month / Year pickers */}
                {(showMonthYearPicker && value.time == "Custom Selection") && (
                    <>
                        <Box sx={{ position: 'relative' }}>
                            <FilterButton onClick={() => !loading && togglePopover("months")} label={"Select Month"} />

                            {isPopoverOpen === "months" && !loading && (
                                <Box sx={{ right: 0, position: 'absolute' }}>
                                    <CustomMonthSelect value={value} search={search} handleSearch={handleSearch} monthsList={filteredMonths} checkMonthSelected={checkMonthSelected} handleMonthSelect={toggleYearMonthHandle} handleAllMonthSelect={handleAllMonthSelection} monthKey={"month"} checkAllMonthSelect={checkAllMonthSelect} colorMode={colorMode} flagValue={"dashboard-data"} checkSearchExist={checkSearchExist} loading={loading} />
                                </Box>
                            )}
                        </Box>

                        <Box sx={{ position: 'relative' }}>
                            <FilterButton onClick={() => !loading && togglePopover("years")} label={"Select Year"} />

                            {isPopoverOpen === "years" && !loading && (
                                <Box sx={{ right: 0, position: 'absolute' }}>
                                    <CustomMonthSelect value={value} search={search} handleSearch={handleSearch} monthsList={filteredYears} checkMonthSelected={checkYearSelected} handleMonthSelect={toggleYearMonthHandle} handleAllMonthSelect={handleAllYearSelection} monthKey={"year"} checkAllMonthSelect={checkAllYearSelect} colorMode={colorMode} flagValue={"dashboard-data"} checkSearchExist={checkSearchExist} loading={loading} />
                                </Box>
                            )}
                        </Box>
                    </>
                )}

                {/* Project Type */}
                <Box sx={{ position: 'relative' }}>
                    <FilterButton onClick={() => !loading && togglePopover("projectType")} label={value.projectType || "Project Type"} />

                    {isPopoverOpen === "projectType" && !loading && (
                        <Box sx={{ position: 'absolute', gap: '8px', right: 2, zIndex: 1, backgroundColor: bgFilter, boxShadow: '0px 0px 16px rgba(0,0,0,0.06)', borderRadius: '4px', fontSize: '15px' }}>
                            <OptionList options={projectTypeOptions} selected={value.projectType || null} onSelect={(opt) => handleOptionClick(opt, "projectType")} />
                        </Box>
                    )}
                </Box>

                {/* Trades */}
                <Box sx={{ position: 'relative' }}>
                    <Button onClick={() => togglePopover("trades")} className="dashboardFilterButton" sx={{ border: "1px solid rgba(141, 141, 141, 0.2)", padding: "8px" }}>
                        <Image src={FilterIcon} />
                        <Text sx={{ fontSize: "14px", marginLeft: '8px', marginRight: '8px', color: 'rgba(141, 141, 141, 1)' }}>{capitalizeFirst(value.trades) || "Trades"}</Text>
                        <Image src={SelectIcon} />
                    </Button>

                    {isPopoverOpen === "trades" && !loading && (
                        <Box sx={{ position: 'absolute', gap: '8px', right: 2, zIndex: 1, backgroundColor: bgFilter, boxShadow: '0px 0px 16px rgba(0,0,0,0.06)', borderRadius: '4px', fontSize: '15px' }}>
                            <TradesOptionList options={tradesOptions} selected={value.trades || null} onSelect={(opt) => handleOptionClick(opt, "trades")} />
                        </Box>
                    )}
                </Box>

                {/* Project Managers */}
                {showProjectManagerList && (
                    <Box sx={{ position: 'relative' }}>
                        <FilterButton onClick={() => !loading && togglePopover("projectManagers")} label={managerDataKey === "projectManager" ? "Select Project Manager" : "Select Task Worker/Manager"} />
                        {isPopoverOpen === "projectManagers" && (<CustomFilterSelect value={value} search={search} handleSearch={handleSearch} projectManagerList={filteredManagersList} checkProjectManagerSelected={managerDataKey === "projectManager" ? checkProjectManagerSelected : checkProjectWorkerSelected} toggleManagerHandle={toggleManagerHandle} handleAllManagerSelection={handleAllManagerSelection} managerDataKey={managerDataKey} flagValue={"dashboard-trades-type-worker-manager"} loading={loading} />)}
                    </Box>
                )}
            </Box>
        </div>
    );
};

export default DashboardFilters;
